/**
 * ScanInput Component
 *
 * Input field optimised for barcode scanning and manual entry.
 * Auto-focuses on mount and handles keyboard/scanner input.
 */

import { forwardRef, useEffect, useRef, useImperativeHandle, type KeyboardEvent } from 'react';
import { TextInput, type TextInputProps, ActionIcon, Group } from '@mantine/core';
import { IconKeyboard, IconX } from '@tabler/icons-react';

export interface ScanInputProps extends Omit<TextInputProps, 'onSubmit'> {
  /** Callback when scan/entry is submitted (Enter pressed or scan complete) */
  onScan?: (value: string) => void;
  /** Whether to auto-focus on mount */
  autoFocus?: boolean;
  /** Whether to clear input after submission */
  clearOnSubmit?: boolean;
  /** Callback to show on-screen keyboard (for kiosk mode) */
  onShowKeyboard?: () => void;
  /** Whether to show the keyboard button (for touch-only terminals) */
  showKeyboardButton?: boolean;
}

export interface ScanInputRef {
  focus: () => void;
  clear: () => void;
  getValue: () => string;
}

/**
 * ScanInput - Input field for barcode scanning and manual entry
 *
 * @example
 * ```tsx
 * <ScanInput
 *   placeholder="Scan equipment barcode..."
 *   onScan={(value) => handleScan(value)}
 *   autoFocus
 *   clearOnSubmit
 * />
 * ```
 */
export const ScanInput = forwardRef<ScanInputRef, ScanInputProps>(function ScanInput(
  {
    onScan,
    autoFocus = true,
    clearOnSubmit = true,
    onShowKeyboard,
    showKeyboardButton = false,
    value,
    onChange,
    ...props
  },
  ref
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const internalValue = useRef('');

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    clear: () => {
      if (inputRef.current) {
        inputRef.current.value = '';
        internalValue.current = '';
      }
    },
    getValue: () => internalValue.current,
  }));

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const currentValue = inputRef.current?.value?.trim() ?? '';

      if (currentValue && onScan) {
        onScan(currentValue);

        if (clearOnSubmit && inputRef.current) {
          inputRef.current.value = '';
          internalValue.current = '';
        }
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    internalValue.current = event.target.value;
    onChange?.(event);
  };

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      internalValue.current = '';
      inputRef.current.focus();
    }
  };

  const rightSection = (
    <Group gap="xs">
      {internalValue.current && (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={handleClear}
          aria-label="Clear input"
        >
          <IconX size={16} />
        </ActionIcon>
      )}
      {showKeyboardButton && onShowKeyboard && (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onShowKeyboard}
          aria-label="Show on-screen keyboard"
        >
          <IconKeyboard size={18} />
        </ActionIcon>
      )}
    </Group>
  );

  return (
    <TextInput
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      rightSection={rightSection}
      rightSectionWidth={showKeyboardButton ? 80 : 40}
      data-testid="scan-input"
      autoComplete="off"
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      {...props}
    />
  );
});
